// 画像ファイルのデコード → 密な RGBA 配列。
// PNG は fast-png でロスレスに (Canvas 経由だと半透明ピクセルの精度が落ちる)。
// SVG は createImageBitmap が扱えないので <img> + Canvas。
// それ以外は createImageBitmap + OffscreenCanvas。

import { decode as decodePng, hasPngSignature } from 'fast-png'
import { MAX_DOCUMENT_DIMENSION } from '@/types/document'

export interface DecodedImage {
  width: number
  height: number
  rgba: Uint8ClampedArray
}

export class ImageTooLargeError extends Error {
  constructor(readonly width: number, readonly height: number) {
    super(`画像が大きすぎます: ${width}x${height}`)
  }
}

export class UnsupportedImageError extends Error {}

export const OPENABLE_IMAGE_TYPES = ['image/png', 'image/jpeg', 'image/webp', 'image/gif', 'image/bmp', 'image/avif', 'image/svg+xml', 'image/tiff']

const MIME_BY_EXT: Record<string, string> = {
  png: 'image/png', jpg: 'image/jpeg', jpeg: 'image/jpeg', webp: 'image/webp', gif: 'image/gif',
  bmp: 'image/bmp', avif: 'image/avif', svg: 'image/svg+xml', tif: 'image/tiff', tiff: 'image/tiff'
}

/** 拡張子から MIME 型を決める。ブリッジ経由のバイト列には型が付かないので補う */
export function mimeForFilename(name: string): string {
  return MIME_BY_EXT[name.split('.').pop()?.toLowerCase() ?? ''] ?? ''
}

export function isImageFile(file: File): boolean {
  if (OPENABLE_IMAGE_TYPES.includes(file.type)) return true
  return /\.(png|jpe?g|webp|gif|bmp|avif|svg|tiff?)$/i.test(file.name)
}

/**
 * ブラウザが重複するダウンロード名に付ける連番。
 * Chrome / Edge は " (2)"、Firefox は "(2)"、Safari は "-2" を**最後の拡張子の手前**に入れる。
 * `.bpe.zip` のような二重拡張子だと `foo.bpe (2).zip` になり、素直に末尾一致させると弾いてしまう。
 */
const DUP = String.raw`(?:[ _]?\(\d+\)|-\d+)?`
const PROJECT_NAME = new RegExp(String.raw`\.bpe${DUP}(\.zip)?$`, 'i')

/** プロジェクト (.bpe.zip / .bpe) のファイル名か */
export function isProjectFileName(name: string): boolean {
  return PROJECT_NAME.test(name)
}

export function isProjectFile(file: File): boolean {
  return isProjectFileName(file.name)
}

function checkSize(w: number, h: number) {
  if (w > MAX_DOCUMENT_DIMENSION || h > MAX_DOCUMENT_DIMENSION) throw new ImageTooLargeError(w, h)
}

/** fast-png の出力 (1/2/3/4 ch, 8/16bit, パレット) を RGBA8 に正規化 */
function pngToRgba(buf: ArrayBuffer): DecodedImage | null {
  const png = decodePng(new Uint8Array(buf))
  const { width, height, channels, depth } = png
  checkSize(width, height)
  if (png.palette) return null // インデックスカラーは Canvas 経路に任せる
  const n = width * height
  const rgba = new Uint8ClampedArray(n * 4)
  const src = png.data
  const scale = depth === 16 ? 1 / 257 : 1
  const get = (i: number) => (depth === 16 ? Math.round(src[i]! * scale) : src[i]!)
  if (depth !== 8 && depth !== 16) return null
  for (let i = 0; i < n; i++) {
    const o = i * 4
    if (channels === 4) {
      rgba[o] = get(i * 4); rgba[o + 1] = get(i * 4 + 1); rgba[o + 2] = get(i * 4 + 2); rgba[o + 3] = get(i * 4 + 3)
    } else if (channels === 3) {
      rgba[o] = get(i * 3); rgba[o + 1] = get(i * 3 + 1); rgba[o + 2] = get(i * 3 + 2); rgba[o + 3] = 255
    } else if (channels === 2) {
      const g = get(i * 2); rgba[o] = g; rgba[o + 1] = g; rgba[o + 2] = g; rgba[o + 3] = get(i * 2 + 1)
    } else {
      const g = get(i); rgba[o] = g; rgba[o + 1] = g; rgba[o + 2] = g; rgba[o + 3] = 255
    }
  }
  return { width, height, rgba }
}

/** 失敗時に「何を渡されたか」を添える。base64 が途中で切れた場合などを見分けられるようにする */
function describeBytes(bytes: Uint8Array): string {
  const head = [...bytes.subarray(0, 8)].map(b => b.toString(16).padStart(2, '0')).join('')
  return `デコードできない画像形式 (${bytes.length} バイト、先頭 ${head || 'なし'})`
}

async function bitmapToRgba(blob: Blob, bytes: Uint8Array): Promise<DecodedImage> {
  let bitmap: ImageBitmap
  try {
    bitmap = await createImageBitmap(blob, { imageOrientation: 'from-image', premultiplyAlpha: 'none', colorSpaceConversion: 'default' })
  } catch {
    throw new UnsupportedImageError(describeBytes(bytes))
  }
  try {
    checkSize(bitmap.width, bitmap.height)
    const canvas = new OffscreenCanvas(bitmap.width, bitmap.height)
    const ctx = canvas.getContext('2d', { willReadFrequently: true })
    if (!ctx) throw new UnsupportedImageError('2D コンテキストが取得できない')
    ctx.drawImage(bitmap, 0, 0)
    const img = ctx.getImageData(0, 0, bitmap.width, bitmap.height)
    return { width: bitmap.width, height: bitmap.height, rgba: img.data }
  } finally {
    bitmap.close()
  }
}

/** TIFF (utif、遅延読み込み)。最初の IFD を RGBA8 にする */
export async function decodeTiff(buf: ArrayBuffer): Promise<DecodedImage> {
  const UTIF = (await import('utif')).default
  const ifds = UTIF.decode(buf)
  if (!ifds.length) throw new UnsupportedImageError('TIFF に画像がありません')
  const ifd = ifds[0]!
  UTIF.decodeImage(buf, ifd)
  const width = ifd.width, height = ifd.height
  if (!(width > 0 && height > 0)) throw new UnsupportedImageError('TIFF のサイズが不正です')
  const rgba = UTIF.toRGBA8(ifd)
  if (rgba.length < width * height * 4) throw new UnsupportedImageError('TIFF を展開できません')
  if (width > MAX_DOCUMENT_DIMENSION || height > MAX_DOCUMENT_DIMENSION) throw new ImageTooLargeError(width, height)
  return { width, height, rgba: new Uint8ClampedArray(rgba.buffer, rgba.byteOffset, rgba.byteLength) }
}

function isTiff(bytes: Uint8Array): boolean {
  return bytes.length >= 4 && ((bytes[0] === 0x49 && bytes[1] === 0x49 && bytes[2] === 0x2a && bytes[3] === 0) || (bytes[0] === 0x4d && bytes[1] === 0x4d && bytes[2] === 0 && bytes[3] === 0x2a))
}

function isSvg(bytes: Uint8Array, type: string): boolean {
  if (type === 'image/svg+xml') return true
  const head = new TextDecoder().decode(bytes.subarray(0, 1024)).trim()
  return /^<(\?xml|!DOCTYPE svg|svg[\s>])/i.test(head) && /<svg[\s>]/i.test(head)
}

/** SVG の内在サイズ。width/height → viewBox の順に見る */
function svgIntrinsicSize(text: string): { width: number; height: number } | null {
  const tag = text.slice(0, 8192).match(/<svg\b[^>]*>/i)?.[0]
  if (!tag) return null
  const attr = (n: string) => tag.match(new RegExp(`\\b${n}\\s*=\\s*["']([^"']+)["']`, 'i'))?.[1]
  const px = (v?: string) => {
    const m = v?.match(/^\s*([0-9.]+)\s*(px)?\s*$/)
    return m ? Number(m[1]) : NaN
  }
  const w = px(attr('width')), h = px(attr('height'))
  if (w > 0 && h > 0) return { width: Math.round(w), height: Math.round(h) }
  const vb = attr('viewBox')?.trim().split(/[\s,]+/).map(Number)
  if (vb?.length === 4 && vb[2]! > 0 && vb[3]! > 0) return { width: Math.round(vb[2]!), height: Math.round(vb[3]!) }
  return null
}

/** SVG のデコード。createImageBitmap は SVG を扱えないので <img> に読ませて Canvas へ描く */
const SVG_DEFAULT_SIZE = 512
async function svgToRgba(bytes: Uint8Array): Promise<DecodedImage> {
  if (typeof Image === 'undefined' || typeof document === 'undefined') throw new UnsupportedImageError('SVG はこの環境ではデコードできない')
  const text = new TextDecoder().decode(bytes)
  const size = svgIntrinsicSize(text) ?? { width: SVG_DEFAULT_SIZE, height: SVG_DEFAULT_SIZE }
  checkSize(size.width, size.height)
  // <img> は MIME 型を見るので、必ず image/svg+xml で作る
  const url = URL.createObjectURL(new Blob([bytes as BlobPart], { type: 'image/svg+xml' }))
  try {
    const img = new Image()
    await new Promise<void>((resolve, reject) => {
      img.onload = () => resolve()
      img.onerror = () => reject(new UnsupportedImageError('SVG を読み込めない (外部参照やスクリプトは使えない)'))
      img.src = url
    })
    const canvas = document.createElement('canvas')
    canvas.width = size.width
    canvas.height = size.height
    const ctx = canvas.getContext('2d', { willReadFrequently: true })
    if (!ctx) throw new UnsupportedImageError('2D コンテキストが取得できない')
    ctx.drawImage(img, 0, 0, size.width, size.height)
    return { width: size.width, height: size.height, rgba: ctx.getImageData(0, 0, size.width, size.height).data }
  } finally {
    URL.revokeObjectURL(url)
  }
}

export async function decodeImageFile(file: Blob): Promise<DecodedImage> {
  const buf = await file.arrayBuffer()
  const bytes = new Uint8Array(buf)
  if (isTiff(bytes)) return decodeTiff(buf)
  if (isSvg(bytes, file.type)) return svgToRgba(bytes)
  if (hasPngSignature(bytes)) {
    try {
      const r = pngToRgba(buf)
      if (r) return r
    } catch (e) {
      if (e instanceof ImageTooLargeError) throw e
      // 壊れた PNG などは Canvas に任せる
    }
  }
  return bitmapToRgba(file, bytes)
}
